import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShoppingCart, Filter, Search, X, Clock, Minus, Plus, Check, Zap } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
import { ShowcaseHeroBackground } from "@/components/ShowcaseHeroBackground";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_DISPLAY = {
  consoles: "Modded Consoles", controllers: "Controllers", cables: "Cables", bundles: "Bundles",
  other: "parts and accessories",
};

const CATEGORY_IMAGES = {
  bundles: "/images/cat-bundles.jpg",
  controllers: "/images/cat-controllers.jpg",
  consoles: "/images/cat-consoles.jpg",
  cables: "/images/cat-cables.jpg",
  other: "/images/cat-other.jpg",
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const searchQuery = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(searchQuery);
  const { addItem, items: cartItems, updateQuantity, removeItem } = useCart();
  const { user } = useAuth();
  const { categories } = useCategories();
  const CATEGORY_LABELS = Object.fromEntries(categories.map(c => [c.key, c.label]));
  const CATEGORY_IMAGE_BY_KEY = Object.fromEntries(categories.map(c => [c.key, c.image_url].filter(Boolean)).filter(([_, v]) => v));
  const navigate = useNavigate();

  // Special-case: the "design" category is a request-form, not a product grid.
  useEffect(() => {
    if (category === "design") navigate("/design-request", { replace: true });
  }, [category, navigate]);

  // Category waitlist state
  const [catWaitlistStatus, setCatWaitlistStatus] = useState(null);
  const [catWaitlistLoading, setCatWaitlistLoading] = useState(false);
  const [catWaitlistMsg, setCatWaitlistMsg] = useState("");

  // Color picker popup
  const [colorPopup, setColorPopup] = useState(null); // product object or null
  const [popupColor, setPopupColor] = useState(null);

  // Category preorder state
  const [showCatPreorder, setShowCatPreorder] = useState(false);
  const [catPreorderColor, setCatPreorderColor] = useState(null);
  const [catPreorderLoading, setCatPreorderLoading] = useState(false);
  const [catPreorderError, setCatPreorderError] = useState("");
  const [catPreorderShipping, setCatPreorderShipping] = useState({
    full_name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US", phone: "",
  });
  const [catPreorderQty, setCatPreorderQty] = useState(1);

  const handleCatPreorder = async () => {
    if (!user) { navigate(`/login?redirect=/products?category=${category}`); return; }
    if (!catPreorderShipping.full_name || !catPreorderShipping.address_line1 || !catPreorderShipping.city || !catPreorderShipping.state || !catPreorderShipping.zip_code) {
      setCatPreorderError("Please fill in all required shipping fields."); return;
    }
    const needsColor = ["consoles", "bundles"].includes(category);
    if (needsColor && !catPreorderColor) {
      setCatPreorderError("Please select a color."); return;
    }
    setCatPreorderLoading(true);
    setCatPreorderError("");
    try {
      const { data } = await axios.post(`${API}/api/preorder/create-session`, {
        product_id: `category:${category}`,
        origin_url: window.location.origin,
        shipping_address: catPreorderShipping,
        quantity: catPreorderQty,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setCatPreorderError(typeof detail === "string" ? detail : "Failed to start preorder");
    } finally {
      setCatPreorderLoading(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (searchQuery) params.search = searchQuery;
      const { data } = await axios.get(`${API}/api/products`, { params });
      setProducts(data);
    } catch (e) {
      console.error("Failed to fetch products", e);
    } finally {
      setLoading(false);
    }
  }, [category, searchQuery]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Check category waitlist status when viewing empty category
  useEffect(() => {
    setCatWaitlistStatus(null);
    setCatWaitlistMsg("");
    if (!category || !user) return;
    const checkCatWaitlist = async () => {
      try {
        const { data } = await axios.get(`${API}/api/waitlist/check/category:${category}`);
        setCatWaitlistStatus(data);
      } catch { /* ignore */ }
    };
    checkCatWaitlist();
  }, [category, user]);

  const handleJoinCatWaitlist = async () => {
    if (!user) {
      navigate(`/login?redirect=/products?category=${category}`);
      return;
    }
    setCatWaitlistLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/waitlist/join`, { product_id: `category:${category}` });
      setCatWaitlistMsg(data.message);
      setCatWaitlistStatus({ on_waitlist: true, entry: { position: data.position } });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setCatWaitlistMsg(typeof detail === "string" ? detail : "Failed to join waitlist");
    } finally {
      setCatWaitlistLoading(false);
    }
  };

  const setCategory = (cat) => {
    const params = {};
    if (cat) params.category = cat;
    if (searchQuery) params.search = searchQuery;
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (category) params.category = category;
    if (searchInput.trim()) params.search = searchInput.trim();
    setSearchParams(params);
  };

  const clearSearch = () => {
    setSearchInput("");
    const params = {};
    if (category) params.category = category;
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      {/* Top hero band — greyed-out showcase photo backdrop behind the page title. */}
      <div className="relative overflow-hidden border-b border-[#1A1A1A]">
        <ShowcaseHeroBackground intervalMs={9000} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Store</p>
              <h1
                data-testid="products-heading"
                className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {category ? CATEGORY_LABELS[category] || category : searchQuery ? "Search Results" : "All Products"}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Search */}
              <form onSubmit={handleSearch} className="relative flex items-center" data-testid="search-form">
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search products..."
                  data-testid="search-input"
                  className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] pr-16 w-48 sm:w-64 h-9 text-sm placeholder:text-[#A1A1A1]/50"
                />
                <div className="absolute right-1 flex items-center gap-1">
                  {searchInput && (
                    <button type="button" onClick={clearSearch} data-testid="clear-search" className="p-1 text-[#A1A1A1] hover:text-red-400">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  <button type="submit" data-testid="search-submit" className="p-1.5 text-[#A1A1A1] hover:text-[#39FF14] transition-colors">
                    <Search className="h-3.5 w-3.5" />
                  </button>
                </div>
              </form>

              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="filter-trigger"
                  variant="outline"
                  className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] hover:bg-transparent text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
                >
                  <Filter className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  {category ? CATEGORY_LABELS[category] : "All"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none">
                <DropdownMenuItem
                  data-testid="filter-all"
                  onClick={() => setCategory("")}
                  className="text-[#F2F2F2] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                >
                  All Products
                </DropdownMenuItem>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <DropdownMenuItem
                    key={key}
                    data-testid={`filter-${key}`}
                    onClick={() => setCategory(key)}
                    className="text-[#F2F2F2] hover:text-[#39FF14] focus:text-[#39FF14] focus:bg-[#141414] rounded-none cursor-pointer"
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-[#050505]">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-[#0D0D0D] aspect-square animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="border border-[#1A1A1A] p-10 sm:p-16 text-center" data-testid="no-products">
            {/* Category image */}
            {category && (CATEGORY_IMAGE_BY_KEY[category] || CATEGORY_IMAGES[category]) && (
              <div className="max-w-xs mx-auto mb-6">
                <div className="aspect-square overflow-hidden border border-[#1A1A1A]">
                  <img src={CATEGORY_IMAGE_BY_KEY[category] || CATEGORY_IMAGES[category]} alt={CATEGORY_LABELS[category] || category} className="w-full h-full object-cover opacity-80" />
                </div>
              </div>
            )}
            {showCatPreorder ? (
              /* Category preorder form */
              <div className="max-w-md mx-auto text-left space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-[#39FF14]" />
                  <p className="text-[#39FF14] font-mono text-xs uppercase tracking-wider font-bold">Preorder {CATEGORY_DISPLAY[category] || category}</p>
                </div>

                {/* Color picker for consoles/bundles */}
                {["consoles", "bundles"].includes(category) && (
                  <div className="mb-4">
                    <p className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-2">Choose Your Color</p>
                    <p className="text-[#A1A1A1] text-xs mb-1">Your selected color will customize the Jewel color, the frontplate Eject button light ring, and the Modchip LED color (visible through the left side of the console).</p>
                    <p className="text-yellow-400 text-xs font-bold mb-3">Custom color adds $20.00 to the price.</p>
                    <div className="flex gap-3 justify-center">
                      {[{ name: "Red", hex: "#EF4444" }, { name: "White", hex: "#F2F2F2" }, { name: "Green", hex: "#39FF14" }, { name: "Blue", hex: "#3B82F6" }, { name: "Yellow", hex: "#EAB308" }].map(({ name, hex }) => (
                        <button key={name} onClick={() => setCatPreorderColor(name)} className={`w-10 h-10 border-2 transition-all ${catPreorderColor === name ? "border-[#F2F2F2] scale-110" : "border-[#1A1A1A] hover:border-[#A1A1A1]"}`} style={{ backgroundColor: hex }} title={name} data-testid={`cat-preorder-color-${name.toLowerCase()}`} />
                      ))}
                    </div>
                    {catPreorderColor && <p className="font-mono text-xs text-[#F2F2F2] mt-2 text-center">Selected: <span className="text-[#39FF14]">{catPreorderColor}</span></p>}
                  </div>
                )}

                <p className="text-[#A1A1A1] text-sm">Fill in your shipping address to complete your preorder via Stripe.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: "full_name", label: "Full Name", span2: true, auto: "name" },
                    { id: "address_line1", label: "Address Line 1", span2: true, auto: "address-line1" },
                    { id: "address_line2", label: "Addr Line 2 (Opt)", span2: true, auto: "address-line2" },
                    { id: "city", label: "City", auto: "address-level2" },
                    { id: "state", label: "State", auto: "address-level1" },
                    { id: "zip_code", label: "ZIP Code", auto: "postal-code" },
                    { id: "country", label: "Country", auto: "country" },
                    { id: "phone", label: "Phone", span2: true, auto: "tel" },
                  ].map(({ id, label, span2, auto }) => (
                    <div key={id} className={span2 ? "sm:col-span-2" : ""}>
                      <label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1] block mb-1">{label}</label>
                      <input name={id} autoComplete={auto} value={catPreorderShipping[id]} onChange={(e) => setCatPreorderShipping(s => ({ ...s, [id]: e.target.value }))} onInput={(e) => setCatPreorderShipping(s => ({ ...s, [id]: e.target.value }))} className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] px-3 py-2 text-sm focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14]" />
                    </div>
                  ))}
                </div>
                {catPreorderError && <p className="text-red-400 text-sm">{catPreorderError}</p>}
                <div className="border border-yellow-600/30 bg-yellow-500/5 p-3">
                  <p className="text-yellow-400 text-xs font-mono uppercase tracking-wider font-bold mb-1">All preorders are final</p>
                  <p className="text-[#A1A1A1] text-xs">By clicking "Preorder" below, you agree that this purchase is non-refundable. Your product is guaranteed and will ship as soon as it's ready.</p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button data-testid="cat-preorder-pay-btn" onClick={handleCatPreorder} disabled={catPreorderLoading || (["consoles","bundles"].includes(category) && !catPreorderColor)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4 disabled:opacity-50">
                    <Zap className="h-3 w-3 mr-2" />{catPreorderLoading ? "Processing..." : "Preorder Now"}
                  </Button>
                  <Button onClick={() => setShowCatPreorder(false)} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider px-6 py-4">Back</Button>
                </div>
              </div>
            ) : catWaitlistStatus?.on_waitlist || catWaitlistMsg ? (
              <div>
                {catWaitlistMsg ? (
                  <p className="text-[#39FF14] text-sm font-mono mb-3">{catWaitlistMsg}</p>
                ) : (
                  <p className="text-[#39FF14] text-sm font-mono mb-3">Great, you are on the waitlist. We will reach out as more become available. Be sure to check your email regularly.</p>
                )}
                <p className="text-[#A1A1A1] text-xs">You can view and manage your waitlist from your <Link to="/cart" className="text-[#39FF14] hover:underline">cart page</Link>.</p>
                <div className="mt-4">
                  <Button data-testid="cat-preorder-btn-alt" onClick={() => user ? setShowCatPreorder(true) : navigate(`/login?redirect=/products?category=${category}`)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                    <Zap className="h-3 w-3 mr-2" />{user ? "Preorder to Guarantee" : "Sign In to Preorder"}
                  </Button>
                </div>
              </div>
            ) : category ? (
              <div>
                <p className="text-[#F2F2F2] text-sm mb-4">
                  Out of stock. We are working as fast as possible to provide more <span className="text-[#39FF14] font-bold">{CATEGORY_DISPLAY[category] || category}</span>.
                </p>
                <p className="text-[#A1A1A1] text-sm mb-6">You can <strong className="text-[#39FF14]">preorder</strong> now to guarantee your product, or join the waitlist to be notified when more are available.</p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button data-testid="cat-preorder-btn" onClick={() => user ? setShowCatPreorder(true) : navigate(`/login?redirect=/products?category=${category}`)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                    <Zap className="h-3 w-3 mr-2" />{user ? "Preorder to Guarantee" : "Sign In to Preorder"}
                  </Button>
                  <Button data-testid="cat-waitlist-yes-btn" onClick={handleJoinCatWaitlist} disabled={catWaitlistLoading} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider px-8 py-4">
                    <Clock className="h-3 w-3 mr-2" />{catWaitlistLoading ? "Joining..." : user ? "Join Waitlist" : "Sign In for Waitlist"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-[#A1A1A1] text-sm">Check back soon for new inventory.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-[#050505]">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="group bg-[#0D0D0D] hover:bg-[#141414] transition-all duration-300"
              >
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square overflow-hidden">
                    {(product.image_urls?.[0] || product.image_url) ? (
                      <img
                        src={product.image_urls?.[0] || product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#0D0D0D]">
                        <span className="font-mono text-[#1A1A1A] text-6xl">X</span>
                      </div>
                    )}
                  </div>
                </Link>
                <div className="p-6 border-t border-[#1A1A1A] group-hover:border-[#39FF14] transition-colors duration-300">
                  <Link to={`/products/${product.id}`}>
                    <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">
                      {CATEGORY_LABELS[product.category] || product.category}
                    </p>
                    <h3 className="text-lg font-bold text-[#F2F2F2] uppercase tracking-tight mb-2 group-hover:text-[#39FF14] transition-colors" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {product.name}
                    </h3>
                  </Link>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-mono text-xl font-bold text-[#39FF14]">
                      ${product.price.toFixed(2)}
                    </span>
                    {product.stock > 0 ? (() => {
                      const inCart = cartItems.find(i => i.product_id === product.id);
                      const qty = inCart ? inCart.quantity : 0;
                      return qty > 0 ? (
                        <div className="flex items-center border border-[#39FF14]" data-testid={`qty-control-${product.id}`}>
                          <button
                            data-testid={`grid-decrease-${product.id}`}
                            onClick={() => qty <= 1 ? removeItem(product.id) : updateQuantity(product.id, qty - 1)}
                            className="px-2.5 py-1.5 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors"
                          >
                            <Minus className="h-3 w-3" strokeWidth={2} />
                          </button>
                          <span className="px-3 py-1.5 font-mono text-xs text-[#39FF14] font-bold border-l border-r border-[#39FF14] min-w-[32px] text-center">
                            {qty}
                          </span>
                          <button
                            data-testid={`grid-increase-${product.id}`}
                            onClick={() => { if (qty < product.stock) updateQuantity(product.id, qty + 1); }}
                            disabled={qty >= product.stock}
                            className="px-2.5 py-1.5 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#39FF14]"
                          >
                            <Plus className="h-3 w-3" strokeWidth={2} />
                          </button>
                        </div>
                      ) : (
                        <Button
                          data-testid={`add-to-cart-${product.id}`}
                          onClick={() => product.color_customizable ? (setColorPopup(product), setPopupColor(null)) : addItem(product)}
                          className="bg-transparent border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] rounded-none font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors duration-200"
                          size="sm"
                        >
                          <ShoppingCart className="h-3 w-3 mr-2" strokeWidth={2} />
                          Add
                        </Button>
                      );
                    })() : (
                      <Link to={`/products/${product.id}`} className="font-mono text-xs text-[#39FF14] hover:text-[#4DFF2E] uppercase tracking-wider transition-colors">
                        Preorder / Waitlist
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Color Picker Popup */}
      {colorPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setColorPopup(null)} data-testid="color-popup-overlay">
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Choose Your Color</h3>
              <button onClick={() => setColorPopup(null)} className="text-[#A1A1A1] hover:text-[#F2F2F2]" data-testid="color-popup-close"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-[#F2F2F2] text-sm font-bold mb-2">{colorPopup.name}</p>
            {(colorPopup.category === "consoles" || colorPopup.category === "bundles") && (
              <div className="mb-4">
                <p className="text-[#A1A1A1] text-xs">Your selected color will customize the Jewel color, the frontplate Eject button light ring, and the Modchip LED color (visible through the left side of the console).</p>
                <p className="text-yellow-400 text-xs font-bold mt-1">Optional — custom color adds $20.00. Leave unselected for stock colors.</p>
              </div>
            )}
            {colorPopup.category === "other" && (
              <div className="mb-4">
                <p className="text-[#A1A1A1] text-xs">Your selected color will be the color of your Xbox Logo accessory.</p>
                <p className="text-yellow-400 text-xs font-bold mt-1">Color selection is required.</p>
              </div>
            )}
            {!["consoles", "bundles", "other"].includes(colorPopup.category) && (
              <p className="text-[#A1A1A1] text-xs mb-4">Select your preferred color for this product.</p>
            )}
            <div className="flex gap-3 mb-5">
              {[
                { name: "Red", hex: "#EF4444" },
                { name: "White", hex: "#F2F2F2" },
                { name: "Green", hex: "#39FF14" },
                { name: "Blue", hex: "#3B82F6" },
                { name: "Yellow", hex: "#EAB308" },
              ].map(({ name, hex }) => (
                <button
                  key={name}
                  onClick={() => setPopupColor(name)}
                  data-testid={`popup-color-${name.toLowerCase()}`}
                  className={`w-10 h-10 border-2 transition-all ${popupColor === name ? "border-[#F2F2F2] scale-110" : "border-[#1A1A1A] hover:border-[#A1A1A1]"}`}
                  style={{ backgroundColor: hex }}
                  title={name}
                />
              ))}
              {popupColor && ["consoles", "bundles"].includes(colorPopup.category) && (
                <button
                  onClick={() => setPopupColor(null)}
                  data-testid="popup-color-none"
                  title="No custom color"
                  className="w-10 h-10 border-2 border-[#1A1A1A] hover:border-[#A1A1A1] flex items-center justify-center text-[#A1A1A1] text-xs font-mono"
                >
                  X
                </button>
              )}
            </div>
            {popupColor && <p className="font-mono text-xs text-[#F2F2F2] mb-4">Selected: <span className="text-[#39FF14]">{popupColor}</span>{["consoles","bundles"].includes(colorPopup.category) ? " (+$20)" : ""}</p>}
            <Button
              data-testid="popup-add-to-cart"
              onClick={() => { addItem(colorPopup, 1, popupColor); setColorPopup(null); }}
              disabled={colorPopup.category === "other" && !popupColor}
              className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-4 disabled:opacity-50"
            >
              <ShoppingCart className="h-3 w-3 mr-2" />
              {popupColor ? `Add to Cart — $${(colorPopup.price + (["consoles","bundles"].includes(colorPopup.category) ? 20 : 0)).toFixed(2)}` : colorPopup.category === "other" ? "Select a Color" : `Add to Cart — $${colorPopup.price.toFixed(2)}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
